import {DeleteObjectCommand, PutObjectCommand, S3Client} from "@aws-sdk/client-s3";

const endpoint = import.meta.env.VITE_R2_ENDPOINT;
const accessKeyId = import.meta.env.VITE_R2_ACCESS_KEY_ID;
const secretAccessKey = import.meta.env.VITE_R2_ACCESS_KEY_SECRET;
const publicBaseUrl = import.meta.env.VITE_R2_PUBLIC_BASE_URL;
const vehicleBucketPublicUrl = import.meta.env.VITE_R2_PUBLIC_VEHICLE_BUCKET_URL;
const dealerBucketPublicUrl = import.meta.env.VITE_R2_PUBLIC_DEALER_BUCKET_URL;

const r2Storage = new S3Client({
    region: "auto",
    endpoint,
    credentials: {
        accessKeyId,
        secretAccessKey,
    },
});

function ensureR2Config() {
    if (!endpoint || !accessKeyId || !secretAccessKey) {
        throw new Error("Missing R2 storage configuration.");
    }

    console.log("[r2Storage] config ready", {
        endpoint,
        hasAccessKeyId: Boolean(accessKeyId),
        hasSecretAccessKey: Boolean(secretAccessKey)
    });
}

const bucketEndpointMap = new Map<string, string>(
    [
        ["vehicle-media", vehicleBucketPublicUrl],
        ["dealer-media", dealerBucketPublicUrl]
    ].filter((entry): entry is [string, string] => Boolean(entry[1]))
);

export function getR2ObjectUrl(bucket: string, key: string) {
    const normalizedKey = key.replace(/^\/+/, "");
    const bucketBaseUrl = bucketEndpointMap.get(bucket);

    if (bucketBaseUrl) {
        return `${bucketBaseUrl.replace(/\/$/, "")}/${normalizedKey}`;
    }

    if (publicBaseUrl) {
        return `${publicBaseUrl.replace(/\/$/, "")}/${bucket}/${normalizedKey}`;
    }

    return `${endpoint.replace(/\/$/, "")}/${bucket}/${normalizedKey}`;
}

export function getR2ObjectKey(bucket: string, url: string) {
    const bucketBaseUrl = bucketEndpointMap.get(bucket);

    if (bucketBaseUrl && url.startsWith(bucketBaseUrl)) {
        return url.slice(bucketBaseUrl.replace(/\/$/, "").length + 1);
    }

    if (publicBaseUrl) {
        const normalizedPublicBaseUrl = publicBaseUrl.replace(/\/$/, "");
        const publicBucketPrefix = `${normalizedPublicBaseUrl}/${bucket}/`;
        if (url.startsWith(publicBucketPrefix)) {
            return url.slice(publicBucketPrefix.length);
        }
    }

    const normalizedEndpoint = endpoint.replace(/\/$/, "");
    const bucketEndpointPrefix = `${normalizedEndpoint}/${bucket}/`;
    if (url.startsWith(bucketEndpointPrefix)) {
        return url.slice(bucketEndpointPrefix.length);
    }

    return undefined;
}

export async function uploadToR2(
    bucket: string,
    key: string,
    body: Blob | File,
    contentType?: string
) {
    ensureR2Config();
    const payload = new Uint8Array(await body.arrayBuffer());
    const resolvedContentType = contentType ?? body.type ?? "application/octet-stream";

    console.log("[r2Storage] upload start", {
        bucket,
        key,
        size: payload.byteLength,
        contentType: resolvedContentType,
        publicUrl: getR2ObjectUrl(bucket, key)
    });

    try {
        const response = await r2Storage.send(new PutObjectCommand({
            Bucket: bucket,
            Key: key,
            Body: payload,
            ContentLength: payload.byteLength,
            ContentType: resolvedContentType,
        }));

        console.log("[r2Storage] upload success", {
            bucket,
            key,
            eTag: response.ETag,
            requestId: response.$metadata.requestId,
            httpStatusCode: response.$metadata.httpStatusCode
        });
    } catch (error) {
        console.error("[r2Storage] upload failed", {
            bucket,
            key,
            error
        });
        throw error;
    }

    return getR2ObjectUrl(bucket, key);
}

export async function deleteFromR2(bucket: string, key: string) {
    ensureR2Config();

    console.log("[r2Storage] delete start", {bucket, key});

    try {
        const response = await r2Storage.send(new DeleteObjectCommand({
            Bucket: bucket,
            Key: key,
        }));

        console.log("[r2Storage] delete success", {
            bucket,
            key,
            requestId: response.$metadata.requestId,
            httpStatusCode: response.$metadata.httpStatusCode
        });
    } catch (error) {
        console.error("[r2Storage] delete failed", {
            bucket,
            key,
            error
        });
        throw error;
    }
}

export default r2Storage;
